<template>
  <div class="mt-2">
    <div class="mb-3 flex items-center justify-between gap-2">
      <DebounceInput
        placeholder="Tìm kiếm bộ cài..."
        @update:model-value="
          (value) =>
            router.push({
              query: {
                ...router.currentRoute.value.query,
                keyword: value,
              },
            })
        "
      />
      <button class="btn btn-primary" @click="isModalOpen = true">
        <i class="fa-solid fa-plus me-2 opacity-90"></i> Tạo bộ cài đặt
      </button>
    </div>
    <template v-if="isLoadingInstallers">
      <LoadingTemplate />
    </template>
    <template
      v-else-if="
        installersData && installersData.data && installersData.data.length > 0
      "
    >
      <PaginationTemplate
        class="mb-3"
        v-if="totalPagesInstallers > 1"
        :total_pages="totalPagesInstallers"
      />

      <div class="bg-base-200 overflow-x-auto">
        <table class="border-base-300 table w-full border">
          <thead class="bg-base-100">
            <tr>
              <th>Phiên bản</th>
              <th>Loại</th>
              <th>Máy chủ 1</th>
              <th>Máy chủ 2</th>
              <th>Ngày tạo</th>
              <th>Hành động</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="installer in installersData.data" :key="installer.id">
              <td>{{ installer.name }}</td>
              <td>{{ installer.type.join(", ") }}</td>
              <td>
                {{ installer.server_1_host }}:{{ installer.server_1_port }}
              </td>
              <td>
                {{ installer.server_2_host }}:{{ installer.server_2_port }}
              </td>
              <td>{{ dayjs(installer.created_at).fromNow() }}</td>
              <td>
                <div class="flex gap-2">
                  <button
                    class="btn"
                    @click="downloadInstaller(installer.id, installer.name)"
                  >
                    <i class="fa-solid fa-download opacity-90"></i>
                    Tải xuống
                  </button>
                  <button
                    class="btn btn-error btn-outline"
                    @click="handleDeleteInstaller(installer.id, installer.name)"
                  >
                    <i class="fa-solid fa-trash opacity-95"></i>
                    Xóa
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
    <template v-else>
      <NotFound />
    </template>
    <CampaignCreateInstallerModal
      :is-open="isModalOpen"
      :campaign-id="String(router.currentRoute.value.params.campaign_id)"
      @close="isModalOpen = false"
      @created="isModalOpen = false"
    />
  </div>
</template>

<script setup lang="ts">
import { number_from_query, str_from_query } from "@/api";
import { deleteVersionAgent, getVersionAgentPagination } from "@/api/campaign";
import DebounceInput from "@/components/DebounceInput.vue";
import CampaignCreateInstallerModal from "@/components/Campaign/CampaignCreateInstallerModal.vue";
import LoadingTemplate from "@/components/LoadingTemplate.vue";
import NotFound from "@/components/NotFound.vue";
import PaginationTemplate from "@/components/PaginationTemplate.vue";
import { downloadFileFromUrl } from "@/composables/useDownload";
import { router } from "@/router/router";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import dayjs from "dayjs";
import "dayjs/locale/vi";
import { computed, reactive, ref, watch } from "vue";
import { toast } from "vue3-toastify";

const isModalOpen = ref(false);
const queryClient = useQueryClient();

const installersFilters = reactive({
  page: number_from_query("page", 1),
  size: number_from_query("size", 24),
  keyword: str_from_query("keyword"),
  campaign: String(router.currentRoute.value.params.campaign_id),
  time_from: undefined as number | undefined,
  time_to: undefined as number | undefined,
});

watch(
  () => [router.currentRoute.value.query],
  () => {
    installersFilters.page = number_from_query("page", 1);
    installersFilters.size = number_from_query("size", 24);
    installersFilters.keyword = str_from_query("keyword");
  },
);

const { data: installersData, isLoading: isLoadingInstallers } = useQuery({
  queryKey: ["installers", installersFilters.campaign, installersFilters],
  queryFn: () => getVersionAgentPagination(installersFilters),
});

const totalPagesInstallers = computed(() => {
  const total = installersData.value?.total_filter || 0;
  return Math.ceil(total / installersFilters.size);
});

const downloadInstaller = (id: string, name: string) => {
  const url = `/api/v1/view/version-agent/${id}`;
  downloadFileFromUrl(url, name);
};

const handleDeleteInstaller = async (id: string, name: string) => {
  const confirmed = confirm(`Bạn có chắc chắn muốn xóa bộ cài đặt "${name}"?`);
  if (!confirmed) return;

  try {
    const success = await deleteVersionAgent(id);
    if (success) {
      toast.success("Xóa bộ cài đặt thành công");
      queryClient.invalidateQueries({
        queryKey: ["installers", installersFilters.campaign],
      });
    } else {
      toast.error("Xóa bộ cài đặt thất bại");
    }
  } catch (error) {
    console.error(error);
    toast.error("Xóa bộ cài đặt thất bại");
  }
};
</script>
